import { Link, useParams } from '@tanstack/react-router';
import { ExternalLink, PackagePlus } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { InventoryLevel, InventoryMovementKind } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useStore } from '@/shared/store/store-context';

import { AdjustStockDialog } from '../components/adjust-stock-dialog';
import type { LevelAction } from '../components/inventory-columns';
import { ItemHistoryCard } from '../components/item-history-card';
import { ItemLevelsCard } from '../components/item-levels-card';
import { ItemSummaryCard } from '../components/item-summary-card';
import { ReorderPointDialog } from '../components/reorder-point-dialog';
import { useInventoryItem } from '../hooks/use-inventory-item';
import { useStockLocations } from '../hooks/use-stock-locations';

const ROUTE = '/_authed/_active/s/$storeId/inventory_/$variantId' as const;

type Dialog =
  | { kind: 'none' }
  | { kind: 'adjust'; level: InventoryLevel }
  | { kind: 'reorderPoint'; level: InventoryLevel };

function InventoryItem() {
  const { t } = useTranslation('inventory');
  const store = useStore();
  const { variantId } = useParams({ from: ROUTE });
  const { can } = usePermissions();
  const canWrite = can('inventory.write');
  const item = useInventoryItem(store.id, variantId);
  const locations = useStockLocations(store.id);
  const [dialog, setDialog] = useState<Dialog>({ kind: 'none' });
  const [historyLocation, setHistoryLocation] = useState<string | null>(null);
  const [historyKind, setHistoryKind] = useState<InventoryMovementKind | null>(null);

  const onAction = useCallback((action: LevelAction, level: InventoryLevel) => {
    if (action === 'movements') {
      setHistoryLocation(level.locationId);
      document.getElementById('inventory-item-history')?.scrollIntoView({ behavior: 'smooth' });
    } else setDialog({ kind: action, level });
  }, []);
  const closeDialog = (open: boolean) => {
    if (!open) setDialog({ kind: 'none' });
  };

  if (item.isPending || locations.isPending) {
    return (
      <PageContent>
        <Skeleton className="h-10 w-72" />
        <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
          <Skeleton className="h-72" />
          <Skeleton className="h-48" />
        </div>
      </PageContent>
    );
  }
  if (item.isError) {
    return <ErrorState error={item.error} onRetry={() => void item.refetch()} />;
  }
  const data = item.data;
  const allLocations = locations.data ?? [];
  const locationOf = (level: InventoryLevel) => allLocations.find((l) => l.id === level.locationId);
  const firstLevel = data.levels[0];

  const productLink = data.storeProductId ? (
    <Link
      to="/s/$storeId/catalog/$storeProductId/variants/$variantId"
      params={{ storeId: store.id, storeProductId: data.storeProductId, variantId }}
    >
      <ExternalLink aria-hidden />
      {t('item.viewProduct')}
    </Link>
  ) : (
    <Link
      to="/org/products/$productId/variants/$variantId"
      params={{ productId: data.productId, variantId }}
    >
      <ExternalLink aria-hidden />
      {t('item.viewProduct')}
    </Link>
  );

  return (
    <PageContent>
      <PageHeader
        title={`${data.productTitle} · ${data.variantTitle}`}
        description={data.sku ? t('item.sku', { sku: data.sku }) : t('item.noSku')}
        backTo={{ to: '/s/$storeId/inventory', params: { storeId: store.id } }}
        backLabel={t('title')}
        tour="inventory.item.header"
        actions={
          <>
            <Button asChild variant="outline">
              {productLink}
            </Button>
            {canWrite && data.trackInventory && firstLevel ? (
              <Button onClick={() => setDialog({ kind: 'adjust', level: firstLevel })}>
                <PackagePlus aria-hidden />
                {t('item.adjust')}
              </Button>
            ) : null}
          </>
        }
      />
      <div className="grid items-start gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="flex min-w-0 flex-col gap-6">
          {data.trackInventory ? (
            <ItemLevelsCard
              storeName={store.name}
              levels={data.levels}
              locations={allLocations}
              canWrite={canWrite}
              onAction={onAction}
            />
          ) : null}
          <div id="inventory-item-history">
            <ItemHistoryCard
              storeId={store.id}
              variantId={variantId}
              locations={allLocations}
              locationId={historyLocation}
              kind={historyKind}
              onLocationChange={setHistoryLocation}
              onKindChange={setHistoryKind}
            />
          </div>
        </div>
        <ItemSummaryCard storeId={store.id} storeName={store.name} item={data} />
      </div>
      {dialog.kind === 'adjust' ? (
        <AdjustStockDialog
          key={dialog.level.locationId}
          storeId={store.id}
          level={dialog.level}
          location={locationOf(dialog.level)}
          locationOptions={data.levels.map((level) => ({
            id: level.locationId,
            name: locationOf(level)?.name ?? '—',
          }))}
          onLocationChange={(locationId) => {
            const next = data.levels.find((level) => level.locationId === locationId);
            if (next) setDialog({ kind: 'adjust', level: next });
          }}
          open
          onOpenChange={closeDialog}
        />
      ) : null}
      {dialog.kind === 'reorderPoint' ? (
        <ReorderPointDialog
          key={dialog.level.locationId}
          storeId={store.id}
          level={dialog.level}
          open
          onOpenChange={closeDialog}
        />
      ) : null}
    </PageContent>
  );
}

/** `/s/$storeId/inventory/$variantId`: one variant's stock at every location, and its history. */
export function InventoryItemPage() {
  return (
    <FeatureErrorBoundary name="inventory.item">
      <InventoryItem />
    </FeatureErrorBoundary>
  );
}
